import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export const NGX_TRANSLATE_PAGINATOR_TOKEN = new InjectionToken<NgxTranslatePaginatorLabels>(
  'NGX_TRANSLATE_PAGINATOR_LABELS'
);

export interface NgxTranslatePaginatorLabels {
  itemsPerPageLabel: string;
  nextPageLabel: string;
  previousPageLabel: string;
  of: string;
  empty_of: string;
}

@Injectable({
  providedIn: 'root',
})
export class NgxTranslatePaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private subscription: Subscription;
  private labels: NgxTranslatePaginatorLabels;

  constructor(
    private translate: TranslateService,
    @Inject(NGX_TRANSLATE_PAGINATOR_TOKEN)
    @Optional()
    labels?: NgxTranslatePaginatorLabels
  ) {
    super();
    this.labels = {
      itemsPerPageLabel: 'paginator.items_per_page',
      nextPageLabel: 'paginator.next_page',
      previousPageLabel: 'paginator.previous_page',
      of: 'paginator.of',
      empty_of: 'paginator.empty_of',
      ...(labels ?? {}),
    };

    this.subscription = this.translate.onLangChange.subscribe(() => {
      this.translateLabels();
    });

    this.translateLabels();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      // 0 of {{length}}
      return this.translate.instant(this.labels.empty_of, {
        length,
      });
    }
    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;

    // `{{start}} - {{end}} of {{length}}`
    return this.translate.instant(this.labels.of, {
      start: startIndex + 1,
      end: endIndex,
      length,
    });
  };

  translateLabels(): void {
    this.itemsPerPageLabel = this.translate.instant(this.labels.itemsPerPageLabel);
    this.nextPageLabel = this.translate.instant(this.labels.nextPageLabel);
    this.previousPageLabel = this.translate.instant(this.labels.previousPageLabel);
    this.changes.next();
  }
}
